<div class="occ">
  @if (!panelOpen()) {
    <button
      type="button"
      class="occ__trigger"
      [class.occ__trigger--active]="lifecycle() === 'active'"
      (click)="togglePanel()"
      aria-label="Open Communication Center"
    >
      <svg cdsIcon="phone" size="16" aria-hidden="true"></svg>
      <span>Communication Center</span>
      @if (lifecycle() === 'active') {
        <span class="occ__trigger-timer" role="timer">{{ elapsedLabel() }}</span>
      }
    </button>
  }

  @if (lifecycle() === 'incoming') {
    <div class="occ__incoming" role="alertdialog" aria-modal="false" aria-labelledby="occ-incoming-title">
      <div class="occ__avatar occ__avatar--lg">{{ displayedCall().initials }}</div>
      <div class="occ__incoming-info">
        <p class="occ__incoming-label">Incoming call</p>
        <p id="occ-incoming-title" class="occ__incoming-name">{{ displayedCall().name }}</p>
        <p class="occ__meta">Phone: {{ displayedCall().phone }}</p>
        @if (displayedCall().location; as location) {
          <p class="occ__meta">Location: {{ location }}</p>
        }
        @if (displayedCall().at; as at) {
          <p class="occ__meta">Call received: {{ at | date: 'MMM d, y, h:mm a' }}</p>
        }
      </div>
      <div class="occ__incoming-actions">
        <button cdsButton="primary" size="sm" type="button" (click)="answerIncoming()">
          <svg cdsIcon="phone" size="16" aria-hidden="true"></svg>
          Answer
        </button>
        <button cdsButton="secondary" size="sm" type="button" (click)="dismissIncoming()">Decline</button>
      </div>
    </div>
  }

  @if (panelOpen()) {
    <section class="occ__panel" role="region" aria-label="Omnichannel Communication Center">
      <header class="occ__panel-header">
        <span class="occ__panel-title">Communication Center</span>
        <button
          cdsButton="ghost"
          size="sm"
          type="button"
          aria-label="Close communication center"
          (click)="closePanel()"
        >
          <svg cdsIcon="close" size="16" aria-hidden="true"></svg>
        </button>
      </header>

      <div class="occ__tabs" role="tablist" aria-label="Communication Center tabs">
        <button
          class="occ__tab"
          role="tab"
          type="button"
          [attr.aria-selected]="activeTab() === 'dial'"
          [class.occ__tab--active]="activeTab() === 'dial'"
          (click)="switchTab('dial')"
        >
          <svg cdsIcon="phone" size="16" aria-hidden="true"></svg>
          Dial Pad
        </button>
        @if (showOnCallTab()) {
          <button
            class="occ__tab occ__tab--oncall"
            role="tab"
            type="button"
            [attr.aria-selected]="activeTab() === 'active'"
            [class.occ__tab--active]="activeTab() === 'active'"
            (click)="switchTab('active')"
          >
            <svg cdsIcon="phone" size="16" aria-hidden="true"></svg>
            On Call
          </button>
        }
        <button
          class="occ__tab"
          role="tab"
          type="button"
          [attr.aria-selected]="activeTab() === 'assist'"
          [class.occ__tab--active]="activeTab() === 'assist'"
          (click)="switchTab('assist')"
        >
          <svg cdsIcon="AI" size="16" aria-hidden="true"></svg>
          AI Assist
        </button>
        <button
          class="occ__tab"
          role="tab"
          type="button"
          [attr.aria-selected]="activeTab() === 'history'"
          [class.occ__tab--active]="activeTab() === 'history'"
          (click)="switchTab('history')"
        >
          <svg cdsIcon="time" size="16" aria-hidden="true"></svg>
          Call History
        </button>
      </div>

      @if (lifecycle() === 'transcription-error') {
        <cds-inline-notification
          [notificationObj]="{
            type: 'error',
            title: 'Transcription unavailable',
            message: transcriptionErrorMessage() + ' You can still take manual notes.',
            lowContrast: true,
            showClose: false,
          }"
        ></cds-inline-notification>
      }

      @switch (activeTab()) {
        @case ('dial') {
          <div class="occ__section" role="tabpanel" aria-label="Dial pad">
            @if (showFakeCallControls() && lifecycle() === 'idle') {
              <button
                cdsButton="secondary"
                size="sm"
                type="button"
                class="occ__simulate"
                (click)="simulateIncomingCall()"
              >
                Simulate incoming call
              </button>
            }
            <div class="occ__quick-contacts">
              <p class="occ__section-label">Quick Contacts</p>
              @for (contact of quickContacts(); track contact.id) {
                <div class="occ__contact-row">
                  <div class="occ__avatar">{{ contact.initials }}</div>
                  <div class="occ__contact-info">
                    <span>{{ contact.name }}</span>
                    <span class="occ__meta">{{ contact.role }} · {{ contact.phone }}</span>
                  </div>
                  <button cdsButton="ghost" size="sm" type="button" (click)="dialContact(contact)">
                    Call
                  </button>
                </div>
              }
            </div>

            <div class="occ__dialer">
              <p class="occ__section-label">Manual Dial</p>
              <div class="occ__dial-display">
                <span>{{ dialBuffer() || 'Enter number…' }}</span>
                <button
                  cdsButton="ghost"
                  size="sm"
                  type="button"
                  aria-label="Delete digit"
                  (click)="dialBackspace()"
                >
                  <svg cdsIcon="delete" size="16" aria-hidden="true"></svg>
                </button>
              </div>
              @if (dialError(); as error) {
                <cds-inline-notification
                  [notificationObj]="{
                    type: 'error',
                    title: 'Callback not started',
                    message: error,
                    lowContrast: true,
                    showClose: false,
                  }"
                ></cds-inline-notification>
              }
              <div class="occ__keypad">
                @for (key of ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#']; track key) {
                  <button class="occ__key" type="button" [attr.aria-label]="key" (click)="dialKey(key)">
                    {{ key }}
                  </button>
                }
              </div>
              <button cdsButton="primary" type="button" class="occ__call-btn" (click)="startManualCall()">
                <svg cdsIcon="phone" size="16" aria-hidden="true"></svg>
                Call
              </button>
            </div>
          </div>
        }

        @case ('active') {
          <div class="occ__section" role="tabpanel" aria-label="Active call">
            <div
              class="occ__caller-banner"
              [class.occ__caller-banner--unmatched]="
                displayedCall().direction !== 'outbound' && !displayedCall().matched
              "
            >
              @if (displayedCall().direction === 'outbound') {
                <div class="occ__avatar occ__avatar--lg">
                  <svg cdsIcon="phone--outgoing" size="16" aria-hidden="true"></svg>
                </div>
                <div class="occ__caller-info">
                  <p class="occ__caller-name">Outbound callback</p>
                  <p class="occ__meta">{{ displayedCall().phone }}</p>
                  @if (outboundStartedLabel(); as startedAt) {
                    <p class="occ__match-status">Started at {{ startedAt }}</p>
                  }
                </div>
              } @else {
                <div class="occ__avatar occ__avatar--lg">{{ displayedCall().initials }}</div>
                <div class="occ__caller-info">
                  <p class="occ__caller-name">{{ displayedCall().name }}</p>
                  <p class="occ__meta">Phone: {{ displayedCall().phone }}</p>
                  @if (displayedCall().location; as location) {
                    <p class="occ__meta">Location: {{ location }}</p>
                  }
                  @if (displayedCall().at; as at) {
                    <p class="occ__meta">Call received: {{ at | date: 'MMM d, y, h:mm a' }}</p>
                  }
                  @if (callerMatchLabel(); as matchLabel) {
                    <p class="occ__match-status">✓ {{ matchLabel }}</p>
                  } @else {
                    <div class="occ__match-status occ__match-status--unmatched">
                      <span>No case record matched.</span>
                      <button cdsButton="ghost" size="sm" type="button" (click)="startHotlineIntake()">
                        Start new hotline intake →
                      </button>
                    </div>
                  }
                </div>
              }
              <div class="occ__timer" role="timer">{{ elapsedLabel() }}</div>
            </div>

            <div class="occ__controls">
              <button
                cdsButton="secondary"
                size="sm"
                type="button"
                [attr.aria-pressed]="muted()"
                (click)="toggleMute()"
              >
                <svg [cdsIcon]="muted() ? 'microphone--off' : 'microphone'" size="16" aria-hidden="true"></svg>
                {{ muted() ? 'Unmute' : 'Mute' }}
              </button>
              <button
                cdsButton="secondary"
                size="sm"
                type="button"
                [attr.aria-pressed]="onHold()"
                (click)="toggleHold()"
              >
                <svg [cdsIcon]="onHold() ? 'play' : 'pause'" size="16" aria-hidden="true"></svg>
                {{ onHold() ? 'Resume' : 'Hold' }}
              </button>
              <button cdsButton="secondary" size="sm" type="button" (click)="openTransferModal()">
                <svg cdsIcon="phone--outgoing" size="16" aria-hidden="true"></svg>
                Transfer
              </button>
              <button cdsButton="danger" size="sm" type="button" (click)="endCall()">
                <svg cdsIcon="phone--off" size="16" aria-hidden="true"></svg>
                End
              </button>
            </div>

            @if (displayedCall().direction !== 'outbound') {
              <div class="occ__transcript-heading">
                <span>Live Transcript</span>
                @if (lifecycle() === 'active') {
                  <span class="occ__transcribing">● AI Transcribing</span>
                }
              </div>
              <div
                class="occ__transcript-live"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {{ latestTranscriptAnnouncement() }}
              </div>
              <div
                #transcriptContainer
                class="occ__transcript"
                role="region"
                tabindex="0"
                (scroll)="onTranscriptScroll()"
                aria-label="Live call transcript"
              >
                @for (message of displayedTranscript(); track message.id) {
                  <p
                    class="occ__line"
                    [class.occ__line--caller]="message.speaker === 'caller'"
                    [class.occ__line--worker]="message.speaker === 'worker'"
                  >
                    <span class="occ__line-speaker">{{ message.speakerLabel }}:</span>
                    {{ message.text }}
                  </p>
                }
              </div>
              @if (lifecycle() === 'active' && displayedTranscript().length > 0) {
                <p class="occ__ai-status" aria-live="polite">AI extracting action items from conversation…</p>
              }
              @if (!isPinnedToBottom()) {
                <button cdsButton="ghost" size="sm" type="button" class="occ__jump" (click)="scrollToLatest()">
                  Jump to latest
                </button>
              }
            }

            <div class="occ__notes">
              <p class="occ__section-label">Call Notes</p>
              <cds-textarea-label ariaLabel="Call notes">
                <textarea
                  cdsTextArea
                  class="occ__notes-field"
                  rows="3"
                  [value]="callNotes()"
                  (input)="onCallNotesInput($event)"
                  placeholder="Type notes during the call — AI also auto-generates a summary."
                ></textarea>
              </cds-textarea-label>
            </div>

            <div class="occ__actions">
              <div class="occ__actions-header">
                <cds-ai-label size="sm" aiText="AI" ariaLabel="AI Recommended Actions"></cds-ai-label>
                <p class="occ__section-label">AI Recommended Actions</p>
              </div>
              @for (action of recommendedActions(); track action.id) {
                <button
                  type="button"
                  class="occ__action"
                  [class.occ__action--done]="isActionCompleted(action.id)"
                  [attr.aria-pressed]="isActionCompleted(action.id)"
                  (click)="triggerAction(action)"
                >
                  <svg
                    [cdsIcon]="action.category === 'Court' ? 'scales' : action.category === 'Verify' ? 'checkmark--filled' : 'task'"
                    size="16"
                    aria-hidden="true"
                  ></svg>
                  <span class="occ__action-text">{{ action.description }}</span>
                  <cds-tag type="outline">{{ action.category }}</cds-tag>
                </button>
              }
            </div>
          </div>
        }

        @case ('assist') {
          <div class="occ__section" role="tabpanel" aria-label="AI Assist">
            <div class="occ__ai-query">
              <cds-text-label ariaLabel="AI assistant query" [hideLabel]="true">
                AI assistant query
                <input
                  cdsText
                  type="text"
                  [value]="aiQuery()"
                  (input)="onAiQueryInput($event)"
                  (keydown.enter)="submitAiQuery()"
                  placeholder="Ask AI anything about this case or policy…"
                />
              </cds-text-label>
              <button
                cdsButton="primary"
                size="sm"
                type="button"
                aria-label="Submit AI query"
                (click)="submitAiQuery()"
              >
                <svg cdsIcon="send" size="16" aria-hidden="true"></svg>
              </button>
            </div>

            <p class="occ__section-label">Quick Launch</p>
            <div class="occ__agent-grid">
              @for (agent of quickLaunchAgents(); track agent.id) {
                <button type="button" class="occ__agent" (click)="launchAgent(agent)">
                  <svg [cdsIcon]="agent.iconGlyph" size="16" aria-hidden="true"></svg>
                  <span class="occ__agent-copy">
                    <span class="occ__agent-name">{{ agent.name }}</span>
                    <span class="occ__meta">{{ agent.description }}</span>
                  </span>
                </button>
              }
            </div>

            @if (aiAnalyzing()) {
              <p class="occ__ai-analyzing" aria-live="polite">● AI analyzing…</p>
            } @else if (aiResponse(); as response) {
              <div class="occ__ai-card" aria-live="polite">
                <div class="occ__ai-card-header">
                  <cds-ai-label size="sm" aiText="AI" ariaLabel="AI Analysis"></cds-ai-label>
                  <span class="occ__ai-card-title">AI Analysis</span>
                </div>
                <p class="occ__ai-card-body">{{ response.summary }}</p>
                @if (response.citations.length > 0) {
                  <div class="occ__ai-citations">
                    @for (citation of response.citations; track citation.label) {
                      <lib-citation-chip [citation]="citation" />
                    }
                  </div>
                }
              </div>
            }
          </div>
        }

        @case ('history') {
          <div class="occ__section" role="tabpanel" aria-label="Call history">
            <p class="occ__section-label">Recent Calls</p>
            @if (callHistory().length === 0) {
              <p>No prior calls for this case yet.</p>
            } @else {
              @for (entry of callHistory(); track entry.id) {
                <div class="occ__history-entry">
                  <svg [cdsIcon]="entry.direction === 'inbound' ? 'phone--incoming' : 'phone'" size="16" aria-hidden="true"></svg>
                  <div class="occ__history-info">
                    <div class="occ__history-top">
                      <span>{{ entry.name }}</span>
                      <span class="occ__meta">{{ entry.durationLabel }}</span>
                    </div>
                    <p class="occ__meta">{{ entry.timestampLabel }}</p>
                    <p>{{ entry.summary }}</p>
                  </div>
                </div>
              }
            }
          </div>
        }
      }
    </section>
  }

  <cds-modal
    size="sm"
    [open]="transferModalOpen()"
    ariaLabel="Transfer call"
    (overlaySelected)="cancelTransfer()"
    (close)="cancelTransfer()"
  >
    <cds-modal-header (closeSelect)="cancelTransfer()">
      <p cdsModalHeaderHeading>Transfer call</p>
    </cds-modal-header>
    <section cdsModalContent>
      <p>Select who to transfer this call to.</p>
      <ul class="occ__transfer-list">
        @for (target of transferTargets(); track target.id) {
          <li>
            <button cdsButton="ghost" type="button" class="occ__transfer-option" (click)="confirmTransfer(target)">
              <span>{{ target.name }}</span>
              <span class="occ__meta">{{ target.role }}</span>
            </button>
          </li>
        }
      </ul>
    </section>
    <cds-modal-footer>
      <button cdsButton="secondary" type="button" (click)="cancelTransfer()">Cancel</button>
    </cds-modal-footer>
  </cds-modal>
</div>
